/* eslint-disable consistent-return */
/* eslint-disable no-restricted-syntax */
import { Button } from 'primereact/button';
import React, { useState, useRef, useEffect } from 'react';

type MultiFileUploadPreviewProps = {
  onFilesSelected: (files: File[]) => void;
  onValidationError?: (errors: string[]) => void;
};

const allowedExtensions = ['.pdf', '.png', '.docx', '.doc'];
const maxFileSize = 5 * 1024 * 1024; // 5 MB
const maxTotalFiles = 5;

const MultiFileUploadPreview: React.FC<MultiFileUploadPreviewProps> = ({ onFilesSelected, onValidationError }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<(string | null)[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (errorMessages.length > 0) {
      const timer = setTimeout(() => setErrorMessages([]), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessages]);

  const getExtension = (fileName: string): string => {
    const parts = fileName.split('.');
    return parts.length > 1 ? `.${parts.pop()?.toLowerCase()}` : '';
  };

  const generatePreviews = (fileList: File[]) => {
    const previewPromises = fileList.map(file => {
      if (file.type.startsWith('image/')) {
        return new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target?.result as string);
          reader.readAsDataURL(file);
        });
      } else {
        return Promise.resolve(null);
      }
    });
    Promise.all(previewPromises).then(setPreviews);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = Array.from(event.target.files || []);
    const currentFileCount = files.length;
    const remainingSlots = maxTotalFiles - currentFileCount;

    const validFiles: File[] = [];
    const rejectedErrors: string[] = [];

    for (const file of inputFiles) {
      const ext = getExtension(file.name);
      if (!allowedExtensions.includes(ext)) {
        rejectedErrors.push(`"${file.name}" has an invalid file type.`);
      } else if (file.size > maxFileSize) {
        rejectedErrors.push(`"${file.name}" exceeds the 5 MB size limit.`);
      } else {
        validFiles.push(file);
      }
    }

    const selectedFiles = validFiles.slice(0, remainingSlots);

    if (validFiles.length > remainingSlots) {
      const isAtStart = currentFileCount === 0;
      const message = isAtStart
        ? `You can only upload a maximum of ${maxTotalFiles} files at once.`
        : `You can only upload a maximum of ${maxTotalFiles} files. ${remainingSlots === 0 ? 'No more files can be added.' : `Only ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} can be added.`}`;
      rejectedErrors.push(message);
    }

    const updatedFiles = [...files, ...selectedFiles];
    setFiles(updatedFiles);
    onFilesSelected(updatedFiles);
    generatePreviews(updatedFiles);
    setErrorMessages(rejectedErrors);

    if (rejectedErrors.length > 0 && onValidationError) {
      onValidationError(rejectedErrors);
    }

    // Clear file input safely
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesSelected(updatedFiles);
    generatePreviews(updatedFiles);
  };

  const getFileLabel = (file: File) => {
    const ext = getExtension(file.name);
    if (ext === '.pdf') return '📄 PDF';
    if (ext === '.doc' || ext === '.docx') return '📝 Word Doc';
    return '📁 File';
  };

  return (
    <div style={{ fontFamily: 'Arial', padding: '1rem' }}>
      <input
        type="file"
        multiple
        accept={allowedExtensions.join(',')}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Button
                  icon="pi pi-paperclip"
                  label="Attach Files"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-primary"
                />
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '10px', color: '#6c757d', fontSize: '12px', verticalAlign: 'middle' }}>
        <i className="pi pi-info-circle" style={{ fontSize: '14px', color: '#0ea5e9' }} />
        <span>
          Allowed: <strong>PDF, PNG, DOC, DOCX</strong> &nbsp;·&nbsp; Max <strong>5 MB</strong> per file &nbsp;·&nbsp; Up to <strong>{maxTotalFiles}</strong> files
        </span>
      </div>

      {errorMessages.length > 0 && (
        <div
          style={{
            marginTop: '1rem',
            background: '#ffe0e0',
            border: '1px solid #ffaaaa',
            borderRadius: '6px',
            padding: '10px',
            color: '#b00000',
            fontSize: '14px',
            maxWidth: '400px',
          }}
        >
          <strong>Upload errors:</strong>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {errorMessages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: '1rem', gap: '10px' }}>
        {files.map((file, index) => (
          <div
            key={index}
            style={{
              position: 'relative',
              width: '120px',
              height: '120px',
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#f9f9f9',
              overflow: 'hidden',
              textAlign: 'center',
              padding: '8px',
              boxSizing: 'border-box',
            }}
            title={file.name}
          >
            {previews[index] ? (
              <img
                src={previews[index]!}
                alt={file.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '14px',
                  padding: '5px',
                  wordBreak: 'break-word',
                }}
              >
                <div>{getFileLabel(file)}</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>{file.name}</div>
              </div>
            )}
            <button
              onClick={() => handleRemoveFile(index)}
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                background: '#ff4d4d',
                border: 'none',
                color: 'white',
                borderRadius: '50%',
                width: '22px',
                height: '22px',
                cursor: 'pointer',
                fontWeight: 'bold',
                lineHeight: '18px',
                padding: 0,
              }}
              aria-label={`Remove ${file.name}`}
              type="button"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultiFileUploadPreview;
